import { useCallback, useEffect, useState } from "react";
import { Menu, SubMenuItem } from "./types/Menu.type";
import { Link, useLocation } from "react-router-dom";
import classNames from "classnames";
import { BREAKPOINTS_NUM } from "@/constants/breakPoints";
import { Typography } from "@/components/kit";
import SvgIcon from "@/components/common/SvgIcon";
import Tooltip from "@/components/kit/Tooltip";
import { useSidebarStore } from "@/hooks/general/useSidebarStore";

type TProps = {
  menuItems: Menu[];
};

function SubMenus({ menuItems }: TProps) {
  const [subMenus, setSubMenus] = useState<Menu["subMenus"]>();
  const { pathname } = useLocation();
  const { showSecondSidebar, isShowingSidebar, setIsShowingSidebar } =
    useSidebarStore();

  // Effect to manage submenus based on current pathname
  useEffect(() => {
    // Find the currently active menu item
    const currentMenu = menuItems.find((menu) => menu.isActive);

    if (currentMenu?.subMenus) {
      setSubMenus(currentMenu.subMenus);
    } else {
      setSubMenus(undefined);
    }
  }, [pathname, menuItems]);

  useEffect(() => {
    const resiseEvent = () => {
      if (window.innerWidth < BREAKPOINTS_NUM["2xl"]) {
        return setIsShowingSidebar("compact");
      }
      setIsShowingSidebar("full");
    };
    window.addEventListener("resize", resiseEvent);

    return () => {
      window.removeEventListener("resize", resiseEvent, false);
    };
  }, []);

  // Handler for closing submenus
  const handleSubMenus = useCallback(() => {
    setIsShowingSidebar(isShowingSidebar === "compact" ? "full" : "compact");
  }, [isShowingSidebar]);

  // Render submenu item component
  const renderSubMenuItem = useCallback(
    (subMenu: SubMenuItem) => {
      // When a submenu path is a prefix of another submenu (e.g. /billing vs /billing/plans-subscriptions),
      // use exact match for the shorter path so both don't activate on the longer route
      const hasChildPath = subMenus?.some(
        (s) => s.path !== subMenu.path && s.path.startsWith(subMenu.path + "/"),
      );
      const isActive = hasChildPath
        ? pathname === subMenu.path || pathname === subMenu.path + "/"
        : pathname.startsWith(subMenu.path);
      if (!subMenu.isSubAccessible) return null;

      return (
        <Tooltip
          arrowPosition="left"
          hidden={isShowingSidebar === "full"}
          className="h-8 !-right-0"
          text={subMenu.title}
        >
          <Link
            key={subMenu.id}
            to={subMenu.path}
            className={classNames(
              "flex w-full items-center gap-1 p-2 rounded-[6px] hover:bg-gray-50 transition-colors",
              { "bg-gray-100": isActive },
            )}
          >
            <SvgIcon
              fill="fill-unset"
              className="w-4 h-4"
              iconName={subMenu.icon}
            />
            {isShowingSidebar === "full" && (
              <Typography
                color="text-gray-700"
                variant={isActive ? "Body2-Bold" : "Body2-Regular"}
              >
                {subMenu.title}
              </Typography>
            )}
          </Link>
        </Tooltip>
      );
    },
    [pathname, isShowingSidebar, subMenus],
  );

  return (
    <>
      {subMenus?.length && (
        <div
          className={classNames(
            "flex gap-6 w-[240px] transition-all duration-300 ease-in-out shrink-0 p-5 flex-col items-center self-stretch rounded-[16px] border-r border-gray-100 bg-white",
            {
              "!w-[72px] items-center": isShowingSidebar === "compact",
              "!max-w-0 !max-h-0 !p-0 opacity-0": !showSecondSidebar,
            },
          )}
        >
          {showSecondSidebar && (
            <>
              {/* Submenu header */}
              <div className="flex w-full gap-2 items-center">
                {isShowingSidebar === "full" && (
                  <>
                    <Typography variant="Title2-Bold">
                      {subMenus[0]?.parentTitle}
                    </Typography>
                    <div className="flex-1" />
                  </>
                )}
                <button
                  onClick={handleSubMenus}
                  className="w-8 h-8 cursor-pointer rounded-[35px] flex p-1 items-center justify-center bg-white hover:bg-gray-50 shadow-md transition-colors"
                  aria-label="Close submenu"
                >
                  <SvgIcon
                    className={classNames("w-5 h-5 transition-all", {
                      "rotate-180": isShowingSidebar === "compact",
                    })}
                    fill="fill-gray-700"
                    iconName="sendSquare"
                  />
                </button>
              </div>

              {/* Submenu items */}
              <div className="flex flex-col w-full gap-4">
                {subMenus.map(renderSubMenuItem)}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default SubMenus;
